import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { IAtributo } from "interfaces/loja/atributosProduto";
import type { IImagemProduto } from "interfaces/loja/produtos";
import type { IVariacaoProdutoState } from "interfaces/loja/variacaoProduto";
import { getAllAtributos } from "services/lojaAtributos";
import { salvarVariacoesProdutoLoja } from "services/lojaProduto";
import {
  gerarCombinacoesVariacoes,
  gerarSKUVariacaoManual,
} from "../../utils/gerarCombinacoesVariacoes";
import { normalizarVariacoesDaApi } from "./helpers";
import { useMaterialUIController, setMessage } from "context";
import VariacoesHeader from "./VariacoesHeader";
import VariacoesTable from "./VariacoesTable";
import VariacaoDrawer from "./VariacaoDrawer";
import ImagensModal from "./ImagensModal";
import ModalGerarVariacoes from "./ModalGerarVariacoes";

export interface VariacoesPageProps {
  produtoId: string;
  produtoSlugOuNome: string;
  /** Nome do produto para geração de SKU (igual ao padrão) */
  produtoNome?: string;
  /** Nome da marca para geração de SKU (igual ao padrão) */
  marcaNome?: string;
  variacoesIniciais: any[];
  imagensProduto: IImagemProduto[];
  onVariacoesSalvas: (variacoes: IVariacaoProdutoState[]) => void;
  onRefetchProduto?: () => Promise<void>;
  /** Preço e preço original da aba Dados para aplicar nas variações geradas automaticamente */
  precoPadrao?: number;
  precoOriginalPadrao?: number;
  /** Preço de custo (CMV) da aba Dados para variação padrão / geradas */
  precoCustoPadrao?: number | null;
}

export default function VariacoesPage({
  produtoId,
  produtoSlugOuNome,
  produtoNome = "PROD",
  marcaNome = "GEN",
  variacoesIniciais,
  imagensProduto,
  onVariacoesSalvas,
  onRefetchProduto,
  precoPadrao = 0,
  precoOriginalPadrao = 0,
  precoCustoPadrao = null,
}: VariacoesPageProps) {
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();

  const [variacoes, setVariacoes] = useState<IVariacaoProdutoState[]>([]);
  const [atributosDisponiveis, setAtributosDisponiveis] = useState<IAtributo[]>(
    []
  );
  const [loadingAtributos, setLoadingAtributos] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawerAberto, setDrawerAberto] = useState(false);
  const [variacaoEmEdicaoIndex, setVariacaoEmEdicaoIndex] = useState<
    number | null
  >(null);
  const [imagensModalIndex, setImagensModalIndex] = useState<number | null>(
    null
  );
  const [modalGerarAberto, setModalGerarAberto] = useState(false);

  useEffect(() => {
    setVariacoes(normalizarVariacoesDaApi(variacoesIniciais));
  }, [variacoesIniciais]);

  useEffect(() => {
    (async () => {
      setLoadingAtributos(true);
      try {
        const res = await getAllAtributos(1, 100);
        const lista = res.data?.data ?? res.data ?? [];
        setAtributosDisponiveis(Array.isArray(lista) ? lista : []);
      } catch (err: any) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: err?.message ?? "Falha ao carregar atributos.",
        });
      } finally {
        setLoadingAtributos(false);
      }
    })();
  }, [dispatch]);

  const handleAbrirGerarAutomaticamente = useCallback(() => {
    setModalGerarAberto(true);
  }, []);

  const handleConfirmarGerarVariacoes = useCallback(
    (atributosSelecionados: IAtributo[]) => {
      const combinacoes = gerarCombinacoesVariacoes(
        atributosSelecionados,
        produtoSlugOuNome,
        {
          preco: precoPadrao,
          precoOriginal: precoOriginalPadrao,
          precoCusto: precoCustoPadrao,
        }
      );
      if (combinacoes.length === 0) {
        setMessage(dispatch, {
          open: true,
          severity: "info",
          text: "Nenhuma combinação gerada. Selecione ao menos um atributo com opções.",
        });
        return;
      }
      const comPadrao = combinacoes.map((c, i) => ({
        ...c,
        isPadrao: i === 0,
      }));
      setVariacoes(comPadrao);
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: `${comPadrao.length} variação(ões) gerada(s). Revise e clique em Salvar variações.`,
      });
    },
    [
      produtoSlugOuNome,
      precoPadrao,
      precoOriginalPadrao,
      precoCustoPadrao,
      dispatch,
    ]
  );

  const handleAdicionar = useCallback(() => {
    setVariacoes((prev) => {
      const skusExistentes = new Set(prev.map((v) => v.sku));
      let sku = gerarSKUVariacaoManual(
        produtoNome,
        marcaNome,
        [],
        atributosDisponiveis
      );
      for (let t = 0; t < 5 && skusExistentes.has(sku); t++) {
        sku = gerarSKUVariacaoManual(
          produtoNome,
          marcaNome,
          [],
          atributosDisponiveis
        );
      }
      return [
        ...prev,
        {
          sku,
          codigoBarras: "",
          preco: precoPadrao,
          precoOriginal: precoOriginalPadrao,
          precoCusto: precoCustoPadrao,
          estoque: 0,
          emEstoque: true,
          isPadrao: false,
          atributos: [],
        },
      ];
    });
  }, [
    produtoNome,
    marcaNome,
    atributosDisponiveis,
    precoPadrao,
    precoOriginalPadrao,
    precoCustoPadrao,
  ]);

  const handleGerenciarAtributos = useCallback(() => {
    navigate("/loja/atributos");
  }, [navigate]);

  const handleSalvarVariacoes = useCallback(async () => {
    if (!produtoId) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Crie o produto na aba Dados antes de salvar variações.",
      });
      return;
    }
    setSaving(true);

    const payload = variacoes.map((v) => ({
      sku: v.sku,
      preco: v.preco,
      precoOriginal: v.precoOriginal ?? 0,
      precoCusto:
        v.precoCusto != null && Number.isFinite(Number(v.precoCusto))
          ? Number(v.precoCusto)
          : null,
      estoque: v.estoque,
      emEstoque: v.isPadrao ? (Number(v.estoque) > 0) : v.emEstoque,
      codigoBarras: v.codigoBarras,
      isPadrao: Boolean(v.isPadrao),
      atributos: v.atributos,
    }));

    try {
      const { data, status } = await salvarVariacoesProdutoLoja(
        produtoId,
        payload
      );

      if (status !== 200) {
        throw new Error(
          "Falha ao salvar variações,verifique se foi definido preço e estoque."
        );
      }

      const variacoesDaResposta = data?.variacoes ?? [];
      const atualizadas = normalizarVariacoesDaApi(variacoesDaResposta);

      if (atualizadas.length > 0) {
        setVariacoes(atualizadas);
        onVariacoesSalvas(atualizadas);
      }
      if (onRefetchProduto) await onRefetchProduto();

      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Variações salvas. Agora você pode carregar fotos em cada variação.",
      });
    } catch (err: any) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: err?.message ?? "Falha ao salvar variações.",
      });
    } finally {
      setSaving(false);
    }
  }, [produtoId, variacoes, onVariacoesSalvas, dispatch]);

  const handleEditar = useCallback((index: number) => {
    setVariacaoEmEdicaoIndex(index);
    setDrawerAberto(true);
  }, []);

  const handleSalvarDrawer = useCallback(
    (index: number, variacao: IVariacaoProdutoState) => {
      setVariacoes((prev) => {
        const updated = prev.map((v, i) =>
          i === index ? { ...variacao, id: v.id } : v
        );
        if (variacao.isPadrao) {
          return updated.map((v, i) => ({
            ...v,
            isPadrao: i === index,
          }));
        }
        return updated;
      });
      setDrawerAberto(false);
      setVariacaoEmEdicaoIndex(null);
    },
    []
  );

  const handleSetPadrao = useCallback((index: number) => {
    setVariacoes((prev) =>
      prev.map((v, i) => ({ ...v, isPadrao: i === index }))
    );
  }, []);

  const handleExcluir = useCallback((index: number) => {
    setVariacoes((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleToggleEmEstoque = useCallback(
    (index: number, emEstoque: boolean) => {
      setVariacoes((prev) =>
        prev.map((v, i) => (i === index ? { ...v, emEstoque } : v))
      );
    },
    []
  );

  const handleAbrirImagens = useCallback(
    (variacaoProdutoId: string | undefined, index: number) => {
      setImagensModalIndex(index);
    },
    []
  );

  const handleImagensEnviadas = useCallback(async () => {
    if (onRefetchProduto) await onRefetchProduto();
  }, [onRefetchProduto]);

  const variacaoEmEdicao =
    variacaoEmEdicaoIndex !== null
      ? (variacoes[variacaoEmEdicaoIndex] ?? null)
      : null;
  const imagensModalVariacao =
    imagensModalIndex !== null ? variacoes[imagensModalIndex] : null;

  return (
    <>
      <VariacoesHeader
        onGerarAutomaticamente={handleAbrirGerarAutomaticamente}
        onAdicionar={handleAdicionar}
        onGerenciarAtributos={handleGerenciarAtributos}
      />
      <ModalGerarVariacoes
        open={modalGerarAberto}
        onClose={() => setModalGerarAberto(false)}
        atributosDisponiveis={atributosDisponiveis}
        onConfirmar={handleConfirmarGerarVariacoes}
      />
      {loadingAtributos ? (
        <p>Carregando atributos…</p>
      ) : (
        <VariacoesTable
          variacoes={variacoes}
          atributos={atributosDisponiveis}
          imagensProduto={imagensProduto}
          onEditar={handleEditar}
          onExcluir={handleExcluir}
          onToggleEmEstoque={handleToggleEmEstoque}
          onSetPadrao={handleSetPadrao}
          onAbrirImagens={handleAbrirImagens}
          onSalvarVariacoes={handleSalvarVariacoes}
          saving={saving}
        />
      )}

      <VariacaoDrawer
        open={drawerAberto}
        onClose={() => {
          setDrawerAberto(false);
          setVariacaoEmEdicaoIndex(null);
        }}
        variacao={variacaoEmEdicao}
        index={variacaoEmEdicaoIndex ?? 0}
        atributos={atributosDisponiveis}
        onSalvar={handleSalvarDrawer}
      />

      <ImagensModal
        open={imagensModalIndex !== null}
        onClose={() => setImagensModalIndex(null)}
        produtoId={produtoId}
        variacaoProdutoId={imagensModalVariacao?.id}
        imagensExistentes={
          imagensModalVariacao?.id
            ? imagensProduto.filter(
                (img) => img.variacaoProdutoId === imagensModalVariacao.id
              )
            : []
        }
        onImagensEnviadas={handleImagensEnviadas}
      />
    </>
  );
}
